import { useEffect, useState, useCallback } from "react";

const STORAGE_KEY = "garage_rm_nav_prefs_v1";

/**
 * Per-user navigation preferences (order + hidden) stored in localStorage.
 * key = STORAGE_KEY + ':' + userId
 */
function readPrefs(userId) {
  if (!userId) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${userId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.warn("navPrefs: failed to read prefs:", err);
    return null;
  }
}

function writePrefs(userId, prefs) {
  if (!userId) return;
  try {
    localStorage.setItem(`${STORAGE_KEY}:${userId}`, JSON.stringify(prefs));
  } catch (err) {
    console.warn("navPrefs: failed to persist prefs (quota?):", err);
  }
}

/**
 * Applies user prefs (order + hidden) to a list of links.
 * Unknown new links are appended at the end (so future features auto-appear).
 */
export function applyNavPrefs(allLinks, prefs) {
  if (!prefs) return { visible: allLinks, hidden: [] };
  const order = Array.isArray(prefs.order) ? prefs.order : [];
  const hidden = new Set(Array.isArray(prefs.hidden) ? prefs.hidden : []);
  const byPath = new Map(allLinks.map((l) => [l.to, l]));
  const ordered = [];
  // First add in saved order, skipping unknown
  order.forEach((to) => {
    if (byPath.has(to)) {
      ordered.push(byPath.get(to));
      byPath.delete(to);
    }
  });
  // Append any new link not yet in saved order
  byPath.forEach((l) => ordered.push(l));
  return {
    visible: ordered.filter((l) => !hidden.has(l.to)),
    hidden: ordered.filter((l) => hidden.has(l.to)),
    ordered,
  };
}

export function useNavPrefs(userId, allLinks) {
  const [prefs, setPrefs] = useState(() => readPrefs(userId));

  // Re-read when user changes
  useEffect(() => {
    setPrefs(readPrefs(userId));
  }, [userId]);

  // Listen to storage events (multi-tab + same-tab dispatch)
  useEffect(() => {
    const handler = (e) => {
      if (!e.key || e.key === `${STORAGE_KEY}:${userId}`) {
        setPrefs(readPrefs(userId));
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [userId]);

  const update = useCallback((next) => {
    const merged = { order: [], hidden: [], ...(prefs || {}), ...next };
    writePrefs(userId, merged);
    setPrefs(merged);
    // dispatch a same-tab event so other components refresh
    window.dispatchEvent(new StorageEvent("storage", { key: `${STORAGE_KEY}:${userId}` }));
  }, [userId, prefs]);

  const reset = useCallback(() => {
    try {
      localStorage.removeItem(`${STORAGE_KEY}:${userId}`);
    } catch (err) {
      console.warn("navPrefs: failed to reset:", err);
    }
    setPrefs(null);
    window.dispatchEvent(new StorageEvent("storage", { key: `${STORAGE_KEY}:${userId}` }));
  }, [userId]);

  const applied = applyNavPrefs(allLinks, prefs);

  return { prefs, applied, update, reset };
}
